import {useCallback, useMemo} from "react";
import {useOktaAuth} from "@okta/okta-react";
import {Intent} from "@blueprintjs/core";
import log from "loglevel";
import {useFeatureFlagContext} from "../providers/FeatureFlagProvider";
import {extractSimplePref, usePreferenceContext} from "../providers/PreferenceProvider";
import {TenantPreferences} from "../constants/constants";
import {getJobEverCompleteStatus} from "../services/TenantService";
import {AppToasterTopLeft} from "../utils/toasters/toaster";

export type EverCompletedJobInfo = {jobId: number; jobNumber: string; orderId?: number};
export type JobInfoMap = Map<number, {jobNumber: string; orderId?: number}>;

export const useJobsEverCompleted = () => {
  const {closedNeverReassign: closedNeverReassignEnabled} = useFeatureFlagContext();
  const {tenantPreferences} = usePreferenceContext();
  const {oktaAuth} = useOktaAuth();

  const isEnabled = useMemo(() => {
    const isClosedNeverReassignPref = extractSimplePref(tenantPreferences, TenantPreferences.closedNeverReassign, false)
      .value as boolean;
    return closedNeverReassignEnabled && isClosedNeverReassignPref;
  }, [closedNeverReassignEnabled, tenantPreferences]);

  const getEverCompletedJobs = useCallback(
    async (jobIds: number[], jobInfoMap: JobInfoMap): Promise<EverCompletedJobInfo[] | null> => {
      if (!isEnabled) return null;

      try {
        const uniqueJobIds = [...new Set(jobIds)];
        const token = oktaAuth.getAccessToken() ?? "";
        const response = await getJobEverCompleteStatus(token, {jobIds: uniqueJobIds});
        const completedJobs = response.data.filter((item) => item.everCompleted);

        return completedJobs.map((item) => {
          const jobInfo = jobInfoMap.get(item.jobId);
          return {
            jobId: item.jobId,
            jobNumber: jobInfo?.jobNumber ?? String(item.jobId),
            orderId: jobInfo?.orderId
          };
        });
      } catch (error) {
        log.error("Error checking job ever completed status", error);
        AppToasterTopLeft.show({
          intent: Intent.DANGER,
          icon: "error",
          message: "Unable to verify job completion status. Please try again.",
          timeout: 5000
        });
        throw error;
      }
    },
    [isEnabled, oktaAuth]
  );

  return {getEverCompletedJobs};
};
